import React, { useState , useEffect, useRef} from 'react';
import { useAuth } from '../hooks/useAuth';
import { useMFA } from '../hooks/useMFA';
import firebase from "../firebase/firebaseConfig";
import edit_icon from "../assets/edit_grey.png";


const Profile = () => {
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'subscription', 'security'
  const [isEditMode, setIsEditMode] = useState(false);
  const [FirstName, setFirstName] = useState("");
  const [LastName, setLastName] = useState("");
  const [industry,setIndustry] = useState("");
  const [company, setCompany] = useState("");
  const [Department, setDepartment] = useState("");
  const [Role, setRole] = useState("");
  const { user,loading } = useAuth();
  const { 
    checkMFAStatus, 
    startPhoneEnrollment, 
    completePhoneEnrollment, 
    unenrollMFA,
    completeUnenrollmentAfterMFA,
    completeReauthenticationWithMFA,
    getEnrolledFactors,
    error: mfaError,
    loading: mfaLoading 
  } = useMFA();
  
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaEnrollmentStep, setMfaEnrollmentStep] = useState('setup'); // 'setup', 'verify', 'enrolled'
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [enrolledFactors, setEnrolledFactors] = useState([]);
  const [resendCooldown, setResendCooldown] = useState(0); // Cooldown in seconds
  const [lastRequestTime, setLastRequestTime] = useState(null);
  const [expireDate, setExpireDate] = useState(null);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordForUnenroll, setPasswordForUnenroll] = useState("");
  const [factorToUnenroll, setFactorToUnenroll] = useState(null);
  const [showUnenrollMfaVerification, setShowUnenrollMfaVerification] = useState(false);
  const [mfaResolverForUnenroll, setMfaResolverForUnenroll] = useState(null);
  const [unenrollMfaVerificationId, setUnenrollMfaVerificationId] = useState("");
  const [unenrollMfaVerificationCode, setUnenrollMfaVerificationCode] = useState("");
  const [unenrollMfaPhoneNumber, setUnenrollMfaPhoneNumber] = useState("");
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaUnenrollRef = useRef(null);
  const recaptchaUnenrollContainerRef = useRef(null);

  const handelSubmit = async(e) =>{
    e.preventDefault();
    const userRef = firebase.database().ref('users/'+ user.uid);
    await userRef.update({
      first_name:FirstName,
      last_name: LastName,
      department:Department,
      company:company,
      role:Role,
      industry:industry
    });
    console.log("User information is updated.");
    setIsEditMode(false);
  }

  const handleCancelEdit = () => {
    // Reset form to original values
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setIndustry(user.industry || "");
    setDepartment(user.department || "");
    setCompany(user.company || "");
    setRole(user.role || "");
    setIsEditMode(false);
  }

  useEffect(() => {
   setFirstName(user.first_name);
   setLastName(user.last_name);
   setIndustry(user.industry)
   setDepartment(user.department);
   setCompany(user.company)
   setRole(user.role)
  },[user]);

  useEffect(() => {
    if (user?.uid) {
      const userRef = firebase.database().ref(`users/${user.uid}/subscription`);
      userRef.once('value', (snapshot) => {
        const subscriptionData = snapshot.val();
        if (subscriptionData?.expireDate) {
          setExpireDate(subscriptionData.expireDate);
        } else if (subscriptionData?.endDate) {
          setExpireDate(subscriptionData.endDate);
        } else {
          setExpireDate(null);
        }
      });
    }
  }, [user]);

  useEffect(() => {
    const checkMFA = async () => {
      if (user) {
        // Check email verification status
        const currentUser = firebase.auth().currentUser;
        if (currentUser) {
          setEmailVerified(currentUser.emailVerified);
        }
        
        const isEnabled = await checkMFAStatus();
        setMfaEnabled(isEnabled);
        if (isEnabled) {
          const factors = getEnrolledFactors();
          setEnrolledFactors(factors);
          setMfaEnrollmentStep('enrolled');
        }
      }
    };
    checkMFA();
  }, [user, checkMFAStatus, getEnrolledFactors]);

  // Cleanup recaptcha verifiers on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
      if (recaptchaUnenrollRef.current) {
        try {
          recaptchaUnenrollRef.current.clear();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  const handleSendEmailVerification = async () => {
    try {
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        await currentUser.sendEmailVerification();
        setEmailVerificationSent(true);
        // Reload user to check verification status
        await currentUser.reload();
        setEmailVerified(currentUser.emailVerified);
      }
    } catch (error) {
      console.error('Error sending email verification:', error);
    }
  };

  const handleStartMFAEnrollment = async (e) => {
    e.preventDefault();
    try {
      // Ensure the container exists
      if (!recaptchaContainerRef.current) {
        throw new Error('reCAPTCHA container not found');
      }

      // Pass the actual DOM element to startPhoneEnrollment
      const result = await startPhoneEnrollment(phoneNumber, recaptchaContainerRef.current);
      setVerificationId(result.verificationId);
      setMfaEnrollmentStep('verify');
    } catch (error) {
      console.error('Error starting MFA enrollment:', error);
    }
  };

  // Cooldown timer effect
  useEffect(() => {
    let interval = null;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  const handleResendCode = async () => {
    // Check cooldown
    const now = Date.now();
    if (lastRequestTime && (now - lastRequestTime) < 60000) {
      const remaining = Math.ceil((60000 - (now - lastRequestTime)) / 1000);
      setResendCooldown(remaining);
      return;
    }

    try {
      if (!recaptchaContainerRef.current) {
        throw new Error('reCAPTCHA container not found. Please refresh the page.');
      }

      if (!recaptchaContainerRef.current.parentNode) {
        throw new Error('reCAPTCHA container is not in the DOM. Please refresh the page.');
      }

      setVerificationCode("");
      setVerificationId("");
      setLastRequestTime(Date.now());
      setResendCooldown(60);
      
      // Resend verification code
      // The hook (useMFA) will handle reCAPTCHA cleanup and recreation
      const result = await startPhoneEnrollment(phoneNumber, recaptchaContainerRef.current);
      setVerificationId(result.verificationId);
    } catch (error) {
      console.error('Error resending code:', error);
      if (error.message && error.message.includes('too many requests')) {
        setResendCooldown(300); // 5 minute cooldown
      }
    }
  };

  const handleCompleteMFAEnrollment = async (e) => {
    e.preventDefault();
    try {
      await completePhoneEnrollment(verificationId, verificationCode);
      setMfaEnabled(true);
      setMfaEnrollmentStep('enrolled');
      setPhoneNumber("");
      setVerificationCode("");
      setVerificationId("");
      
      // Refresh enrolled factors
      const factors = getEnrolledFactors();
      setEnrolledFactors(factors);

      // reCAPTCHA cleanup is handled by the useMFA hook
    } catch (error) {
      console.error('Error completing MFA enrollment:', error);
    }
  };

  const handleUnenrollMFA = async (factorUid) => {
    // Show password prompt first
    setFactorToUnenroll(factorUid);
    setShowPasswordPrompt(true);
    setPasswordForUnenroll("");
  };

  const confirmUnenrollMFA = async () => {
    if (!passwordForUnenroll || !factorToUnenroll) {
      return;
    }

    try {
      const result = await unenrollMFA(factorToUnenroll, passwordForUnenroll);
      
      // Check if MFA verification is required for re-authentication
      if (result.requiresMFA) {
        // Store the resolver and factor for later use
        setMfaResolverForUnenroll(result.resolver);
        setFactorToUnenroll(result.factorUid);
        
        // Hide password prompt and show MFA verification UI
        setShowPasswordPrompt(false);
        setShowUnenrollMfaVerification(true);
        
        // Automatically send verification code
        setTimeout(() => {
          handleSendUnenrollMfaVerificationCode(result.resolver);
        }, 100);
        
        return;
      }
      
      // If successful without MFA, clean up
      if (result.success) {
        setMfaEnabled(false);
        setMfaEnrollmentStep('setup');
        setEnrolledFactors([]);
        setShowPasswordPrompt(false);
        setPasswordForUnenroll("");
        setFactorToUnenroll(null);
      }
    } catch (error) {
      console.error('Error unenrolling MFA:', error);
      // Error is already set in the hook
    }
  };

  const handleSendUnenrollMfaVerificationCode = async (resolver) => {
    try {
      const resolverToUse = resolver || mfaResolverForUnenroll;
      if (!resolverToUse) {
        throw new Error('MFA resolver not available');
      }

      // Ensure the container exists
      if (!recaptchaUnenrollContainerRef.current) {
        throw new Error('reCAPTCHA container not found');
      }

      // Clear old verifier
      if (recaptchaUnenrollRef.current) {
        try {
          recaptchaUnenrollRef.current.clear();
        } catch (e) {
          // Ignore
        }
        recaptchaUnenrollRef.current = null;
      }

      // Clear container
      try {
        while (recaptchaUnenrollContainerRef.current.firstChild) {
          recaptchaUnenrollContainerRef.current.removeChild(recaptchaUnenrollContainerRef.current.firstChild);
        }
      } catch (e) {
        try {
          recaptchaUnenrollContainerRef.current.innerHTML = '';
        } catch (e2) {
          console.warn('Could not clear reCAPTCHA container:', e2);
        }
      }

      // Create new verifier
      recaptchaUnenrollRef.current = new firebase.auth.RecaptchaVerifier(
        recaptchaUnenrollContainerRef.current,
        {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => {
            console.error('reCAPTCHA expired');
          }
        }
      );

      // Render reCAPTCHA
      await recaptchaUnenrollRef.current.render();
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Get enrolled factors and session
      const enrolledFactors = resolverToUse.hints;
      if (!enrolledFactors || enrolledFactors.length === 0) {
        throw new Error('No enrolled MFA factors found');
      }

      const multiFactorHint = enrolledFactors[0];
      setUnenrollMfaPhoneNumber(multiFactorHint.phoneNumber || 'Phone Number');

      // Get session
      let session;
      if (typeof resolverToUse.session === 'function') {
        session = await resolverToUse.session();
      } else if (resolverToUse.session) {
        session = resolverToUse.session;
      } else {
        throw new Error('Resolver session not available');
      }

      // Send verification code
      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      const verificationId = await phoneAuthProvider.verifyPhoneNumber({
        multiFactorHint: multiFactorHint,
        session: session
      }, recaptchaUnenrollRef.current);

      setUnenrollMfaVerificationId(verificationId);
    } catch (error) {
      console.error('Error sending verification code:', error);
      if (recaptchaUnenrollRef.current) {
        try {
          recaptchaUnenrollRef.current.clear();
        } catch (e) {
          // Ignore
        }
        recaptchaUnenrollRef.current = null;
      }
    }
  };

  const handleVerifyUnenrollMfaCode = async () => {
    if (!unenrollMfaVerificationCode || !unenrollMfaVerificationId || !mfaResolverForUnenroll) {
      return;
    }

    try {
      // Complete re-authentication with MFA
      await completeReauthenticationWithMFA(
        mfaResolverForUnenroll,
        unenrollMfaVerificationCode,
        unenrollMfaVerificationId
      );

      // Now complete the unenrollment
      const result = await completeUnenrollmentAfterMFA(factorToUnenroll);
      
      if (result.success) {
        // Clean up all state
        setMfaEnabled(false);
        setMfaEnrollmentStep('setup');
        setEnrolledFactors([]);
        setShowPasswordPrompt(false);
        setShowUnenrollMfaVerification(false);
        setPasswordForUnenroll("");
        setFactorToUnenroll(null);
        setMfaResolverForUnenroll(null);
        setUnenrollMfaVerificationId("");
        setUnenrollMfaVerificationCode("");
        setUnenrollMfaPhoneNumber("");
        
        // Clean up verifier
        if (recaptchaUnenrollRef.current) {
          try {
            recaptchaUnenrollRef.current.clear();
          } catch (e) {
            // Ignore
          }
          recaptchaUnenrollRef.current = null;
        }
      }
    } catch (error) {
      console.error('Error verifying MFA code:', error);
    }
  };

  const cancelUnenrollMFA = () => {
    setShowPasswordPrompt(false);
    setPasswordForUnenroll("");
    setFactorToUnenroll(null);
  };
  

  return (
    <>
      <div className="flex justify-between items-center w-full mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
      </div>
      
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex">
          <button
            onClick={() => {
              setActiveTab('profile');
              setIsEditMode(false);
            }}
            className={`px-6 py-4 text-base font-medium transition-colors ${
              activeTab === 'profile'
                ? 'text-[#F58F29] border-b-2 border-[#F58F29]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            User Profile
          </button>
          <button
            onClick={() => {
              setActiveTab('subscription');
              setIsEditMode(false);
            }}
            className={`px-6 py-4 text-base font-medium transition-colors ${
              activeTab === 'subscription'
                ? 'text-[#F58F29] border-b-2 border-[#F58F29]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Subscription
          </button>
          <button
            onClick={() => {
              setActiveTab('security');
              setIsEditMode(false);
            }}
            className={`px-6 py-4 text-base font-medium transition-colors ${
              activeTab === 'security'
                ? 'text-[#F58F29] border-b-2 border-[#F58F29]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Security
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-8">
          {/* User Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                {!isEditMode && (
                  <button
                    onClick={() => setIsEditMode(true)}
                    className="flex items-center text-gray-600 hover:text-gray-900"
                  >
                    <img src={edit_icon} alt="Edit" className="w-5 h-5" />
                  </button>
                )}
              </div>

              {!isEditMode ? (
                // Read-only view
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        First Name
                      </label>
                      <p className="text-sm text-gray-900">{FirstName || '—'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Last Name
                      </label>
                      <p className="text-sm text-gray-900">{LastName || '—'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Industry
                      </label>
                      <p className="text-sm text-gray-900">{industry || '—'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Company
                      </label>
                      <p className="text-sm text-gray-900">{company || '—'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Department
                      </label>
                      <p className="text-sm text-gray-900">{Department || '—'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Role
                      </label>
                      <p className="text-sm text-gray-900">{Role || '—'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                // Edit mode - show form
                <form className="space-y-6" onSubmit={handelSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="first-name">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="first-name"
                        name="first-name"
                        value={FirstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="last-name">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="last-name"
                        name="last-name"
                        value={LastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="industry">
                        Industry
                      </label>
                      <select
                        id="industry"
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                      >
                        <option value="">Select Industry</option>
                        <option value="Technology">Technology</option>
                        <option value="Finance">Finance</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Education">Education</option>
                        <option value="Retail">Retail</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Non-Profit">Non-Profit</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="company">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="department">
                        Department
                      </label>
                      <select
                        id="department"
                        value={Department}
                        onChange={e => setDepartment(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                      >
                        <option value="">Select Department</option>
                        <option value="Engineering">Engineering</option>
                        <option value="Product">Product</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Finance">Finance</option>
                        <option value="Customer Success">Customer Success</option>
                        <option value="Business Operation">Business Operation</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="role">
                        Role
                      </label>
                      <input
                        type="text"
                        id="role"
                        name="role"
                        value={Role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#F58F29] text-white font-medium rounded-md shadow-sm hover:bg-[#E57E1F] focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:ring-offset-2 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-500 mb-1">Current Plan</p>
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-bold text-gray-900 capitalize">
                      {user.planType || 'No Plan'}
                    </p>
                    {user.planType && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <span className="w-1.5 h-1.5 mr-1.5 bg-green-400 rounded-full"></span>
                        Active
                      </span>
                    )}
                  </div>
                </div>
                {user.planType && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500 mb-1">Expire Date</p>
                    <p className="text-sm text-gray-900">
                      {expireDate 
                        ? new Date(expireDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : '—'}
                    </p>
                  </div>
                )}
                {!user.planType && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      You don't have an active subscription. Visit the pricing page to select a plan.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Multi-Factor Authentication</h2>
                <p className="text-sm text-gray-600">
                  Add an extra layer of security to your account by enabling multi-factor authentication.
                </p>
              </div>

              {/* MFA Setup Step */}
              {mfaEnrollmentStep === 'setup' && (
                <div className="space-y-6">
                  {!emailVerified && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-sm font-medium text-yellow-800">Email Verification Required</h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>You must verify your email address before enabling multi-factor authentication.</p>
                            {emailVerificationSent ? (
                              <div className="mt-3">
                                <p className="text-green-700 font-medium mb-1">✓ Verification email sent!</p>
                                <p className="text-xs text-yellow-600 mb-2">Please check your inbox and click the verification link.</p>
                                <button
                                  type="button"
                                  onClick={() => window.location.reload()}
                                  className="text-sm font-medium text-gray-600 hover:text-gray-900 underline"
                                >
                                  Refresh Page
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={handleSendEmailVerification}
                                disabled={mfaLoading}
                                className="mt-3 px-4 py-2 bg-[#F58F29] text-white font-medium rounded-md shadow-sm hover:bg-[#E57E1F] focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {mfaLoading ? 'Sending...' : 'Send Verification Email'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {mfaError && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-sm font-medium text-red-800">Error</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{mfaError}</p>
                            {mfaError.includes('EMAIL_NOT_VERIFIED') || mfaError.includes('unverified-email') ? (
                              <div className="mt-2">
                                {!emailVerificationSent && (
                                  <button
                                    type="button"
                                    onClick={handleSendEmailVerification}
                                    disabled={mfaLoading}
                                    className="text-sm font-medium text-gray-600 hover:text-gray-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Send Verification Email
                                  </button>
                                )}
                              </div>
                            ) : mfaError.includes('not enabled') ? (
                              <div className="mt-3 text-xs">
                                <p className="font-semibold mb-2">To enable Phone Authentication:</p>
                                <ol className="list-decimal list-inside space-y-1 ml-2">
                                  <li>Go to <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer" className="underline">Firebase Console</a></li>
                                  <li>Select your project</li>
                                  <li>Navigate to Authentication → Sign-in method</li>
                                  <li>Click on "Phone" provider</li>
                                  <li>Enable it and save</li>
                                </ol>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleStartMFAEnrollment} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="phone">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="+1234567890"
                        required
                        value={phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\+?[\d\s\-\(\)]+$/.test(value)) {
                            setPhoneNumber(value);
                          }
                        }}
                        disabled={!emailVerified || mfaLoading}
                        pattern="^\+[1-9]\d{1,14}$"
                        title="Phone number must start with + followed by country code and number (e.g., +14155552671)"
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        Format: Start with +, then country code and number (e.g., +14155552671 for US, +442071234567 for UK)
                      </p>
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        disabled={!emailVerified || mfaLoading}
                        className="px-4 py-2 bg-[#F58F29] text-white font-medium rounded-md shadow-sm hover:bg-[#E57E1F] focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {mfaLoading ? 'Processing...' : 'Enable MFA'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* MFA Verification Step */}
              {mfaEnrollmentStep === 'verify' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                    <p className="text-sm text-blue-700">
                      Enter the verification code sent to <span className="font-medium">{phoneNumber}</span>
                    </p>
                  </div>

                  {mfaError && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-sm font-medium text-red-800">Error</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{mfaError}</p>
                            {(mfaError.includes('expired') || mfaError.includes('invalid')) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleResendCode();
                                }}
                                disabled={mfaLoading || resendCooldown > 0}
                                className="mt-2 text-sm font-medium text-gray-600 hover:text-gray-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend verification code'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleCompleteMFAEnrollment} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="code">
                        Verification Code
                      </label>
                      <input
                        type="text"
                        id="code"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                        placeholder="123456"
                        required
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        maxLength="6"
                      />
                    </div>

                    <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => {
                          setMfaEnrollmentStep('setup');
                          setVerificationCode("");
                          setVerificationId("");
                          // reCAPTCHA cleanup is handled by the useMFA hook
                        }}
                        className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={mfaLoading || resendCooldown > 0}
                        className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend Code'}
                      </button>
                      <button
                        type="submit"
                        disabled={mfaLoading}
                        className="px-4 py-2 bg-[#F58F29] text-white font-medium rounded-md shadow-sm hover:bg-[#E57E1F] focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* MFA Enrolled Step */}
              {mfaEnrollmentStep === 'enrolled' && (
                <div className="space-y-6">
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Multi-factor authentication is enabled</h3>
                        <p className="mt-1 text-sm text-green-700">Your account is now protected with an additional layer of security.</p>
                      </div>
                    </div>
                  </div>

                  {enrolledFactors.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-3">Enrolled Factors</h3>
                      <div className="space-y-3">
                        {enrolledFactors.map((factor, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {factor.phoneNumber || factor.displayName || `Phone Number ${index + 1}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Enrolled {factor.enrollmentTime ? new Date(factor.enrollmentTime).toLocaleDateString() : ''}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnenrollMFA(factor.uid)}
                              disabled={mfaLoading}
                              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-300 hover:bg-red-50 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {mfaLoading ? 'Removing...' : 'Remove'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* reCAPTCHA Container - always rendered when Security tab is active */}
              <div 
                id="recaptcha-container" 
                ref={recaptchaContainerRef} 
                style={{ 
                  position: 'absolute', 
                  left: '-9999px', 
                  visibility: 'hidden',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden'
                }}
                aria-hidden="true"
              ></div>
            </div>
          )}
        </div>
      </div>

      {/* Password Prompt Modal for MFA Unenrollment */}
      {showPasswordPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Removal</h3>
            <p className="text-sm text-gray-600 mb-4">
              Removing multi-factor authentication requires recent authentication. Please enter your password to continue.
            </p>
            
            {mfaError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-md mb-4">
                <p className="text-sm text-red-700">{mfaError}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="password">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={passwordForUnenroll}
                onChange={(e) => setPasswordForUnenroll(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                placeholder="Enter your password"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && passwordForUnenroll) {
                    confirmUnenrollMFA();
                  }
                }}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelUnenrollMFA}
                disabled={mfaLoading}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnenrollMFA}
                disabled={!passwordForUnenroll || mfaLoading}
                className="px-4 py-2 bg-[#F58F29] text-white font-medium rounded-md shadow-sm hover:bg-[#E57E1F] focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mfaLoading ? 'Removing...' : 'Confirm Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFA Verification Modal for Unenrollment */}
      {showUnenrollMfaVerification && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Verify Your Identity</h3>
            <p className="text-sm text-gray-600 mb-4">
              To remove multi-factor authentication, you must first verify your identity. 
              {unenrollMfaPhoneNumber && ` A verification code has been sent to ${unenrollMfaPhoneNumber}.`}
            </p>
            
            {mfaError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-md mb-4">
                <p className="text-sm text-red-700">{mfaError}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="verification-code">
                Verification Code
              </label>
              <input
                type="text"
                id="verification-code"
                value={unenrollMfaVerificationCode}
                onChange={(e) => setUnenrollMfaVerificationCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:border-transparent"
                placeholder="Enter 6-digit code"
                autoFocus
                maxLength={6}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && unenrollMfaVerificationCode) {
                    handleVerifyUnenrollMfaCode();
                  }
                }}
              />
            </div>

            <div 
              id="recaptcha-unenroll-container" 
              ref={recaptchaUnenrollContainerRef}
              style={{ 
                position: 'absolute', 
                left: '-9999px', 
                visibility: 'hidden',
                width: '1px',
                height: '1px',
                overflow: 'hidden'
              }}
              aria-hidden="true"
            ></div>

            <div className="flex justify-between items-center mb-4">
              <button
                type="button"
                onClick={() => handleSendUnenrollMfaVerificationCode()}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={mfaLoading}
              >
                Resend Code
              </button>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUnenrollMfaVerification(false);
                  setShowPasswordPrompt(false);
                  setPasswordForUnenroll("");
                  setFactorToUnenroll(null);
                  setMfaResolverForUnenroll(null);
                  setUnenrollMfaVerificationId("");
                  setUnenrollMfaVerificationCode("");
                  setUnenrollMfaPhoneNumber("");
                  if (recaptchaUnenrollRef.current) {
                    try {
                      recaptchaUnenrollRef.current.clear();
                    } catch (e) {
                      // Ignore
                    }
                    recaptchaUnenrollRef.current = null;
                  }
                }}
                disabled={mfaLoading}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyUnenrollMfaCode}
                disabled={!unenrollMfaVerificationCode || unenrollMfaVerificationCode.length !== 6 || mfaLoading}
                className="px-4 py-2 bg-[#F58F29] text-white font-medium rounded-md shadow-sm hover:bg-[#E57E1F] focus:outline-none focus:ring-2 focus:ring-[#F58F29] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mfaLoading ? 'Verifying...' : 'Verify & Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
};

export default Profile;
